import {UI} from "../../../../../stem-core/src/ui/UIBase";

import {BasePanelPage, BasePanelPageStyle} from "../../../PanelPage";
import {styleRule, styleRuleInherit} from "../../../../../stem-core/src/decorators/Style";
import {getWindowWidth, isSmallScreen, NOOP_FUNCTION} from "../../../../UtilsLib";
import {merchantStyleRule} from "../../../Security";
import {iFrameState} from "../../../../services/IFrameState";
import {registerStyle} from "../../../../../stem-core/src/ui/style/Theme";
import {SubscriptionCoverageCard} from "./components/SubscriptionCoverageCard";
import {iFrameMerchantService} from "../../../../services/IframeMerchantService";
import {DiscountCodeInput} from "./components/DiscountCodeInput";
import {SOUTHERN_THING_ALIAS, TNR_ALIAS} from "../../../../../blink-sdk/Constants";
import {CheckboxInput} from "../../../../../core/ui/input/checkbox/CheckboxInput.jsx";
import {Messages} from "../../../../Messages";
import {PoweredByBlink} from "../../../panel-elements/PoweredByBlink.jsx";
import {UserAddressStore} from "../../../../../client/state/UserAddressStore";
import {SUBSCRIBE_PLAN_STATE} from "../../../flows/SubscriptionPlanController.js";
import {CoverageChangeType} from "../../../../../client/state/SubscriptionCoverageStore";
import {SubscriptionApplyPolicy} from "../../../../../client/state/SubscriptionStore";
import {TNRSubscriptionCoverageCard} from "./components/TNRSubscriptionCoverageCard";
import {SubscriptionOfferStore} from "../../../../../client/state/SubscriptionOfferStore";
import {iFrameUserDataService} from "../../../../services/IFrameUserDataService";
import {
    AnalyticsEventType,
    dispatchAnalyticsEvent
} from "../../../../../blink-sdk/utils/AnalyticsClient";
import {PanelText} from "../../../panel-elements/PanelText.jsx";
import {Controller} from "../../../flows/Controller.js";
import {SubscriptionFlowHandler} from "../../../flows/SubscriptionFlowHandler.js";
import {iFrameAuthService} from "../../../../services/IFrameAuthService";
import {PanelImage} from "../../../panel-elements/PanelImage.jsx";


function getSubscriptionPlanMaxWidth() {
    return iFrameMerchantService.isMerchant(TNR_ALIAS) ? 936 : 1272;
}

class SubscriptionPlanPanelStyle extends BasePanelPageStyle {
    @styleRuleInherit
    content = {
        width: null
    }

    @styleRule
    coveragesList = {
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 24,
        maxWidth: "100%",
    };

    @styleRule
    extraPadding = {
        paddingLeft: () => (isSmallScreen() ? 0 : 144),
        paddingRight: () => (isSmallScreen() ? 0 : 144),
    };

    @styleRule
    coveragesRow = {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        flexWrap: () => getWindowWidth() < getSubscriptionPlanMaxWidth() ? "wrap" : "",
    };

    @styleRule
    coverageCardContainer = {
        paddingBottom: 24,
        ">*": {
            height: "100%",
        },
        paddingRight: isSmallScreen() ? 0 : 12,
        paddingLeft: isSmallScreen() ? 0 : 12,
    };

    @styleRule
    discountCodeInput = {
        marginBottom: 24,
    };

    @styleRule
    shareNameCheckbox = {
        fontSize: () => this.themeProps.MERCHANT_FONT_SIZE_LARGE + " !important",
        fontWeight: this.themeProps.MERCHANT_FONT_WEIGHT_LIGHT,
        margin: "0 auto 12px",
        maxWidth: 524,
        display: "flex",
    };

    messageObject = {
        color: this.themeProps.MERCHANT_TEXT_SECONDARY_COLOR,
        textAlign: "center",
        marginBottom: () => (isSmallScreen() ? 18 : 24),
        width: "100%",
        ":last-child": {
            marginBottom: 0,
        },
    };

    @styleRule
    message = {
        ...this.messageObject,
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_LARGE,
    };

    @merchantStyleRule
    panelTextStyle = {
        ...iFrameState.textStyle,
    };

    @merchantStyleRule
    panelFooterSeparatorStyle = {
        marginLeft: "auto",
        marginRight: "auto",
        ...iFrameState.footerSeparatorStyle,
    };

    @merchantStyleRule
    panelFooterTextStyle = {
        marginLeft: "auto",
        marginRight: "auto",
        ...iFrameState.footerTextStyle,
    };

    @styleRule
    subscriptionText = {
        margin: "18px 0px",
        display: "flex"
    }

    @styleRule
    subscribeImageLogo = {
        maxWidth: "20%",
        margin: "auto",
        marginTop: 0
    }
}

@registerStyle(SubscriptionPlanPanelStyle)
export class SubscriptionPlanPanel extends BasePanelPage {
    analyticsDispatched = false;

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        attr.setStyle("maxWidth", Math.min(iFrameState.screen.width, getSubscriptionPlanMaxWidth()));
        attr.setStyle("width", "max-content");
    }

    getAvailableSubscriptionCoveragesListElement() {
        const {styleSheet} = this;

        // TODO: Have a special ordering field instead of "id".
        const subscriptionCoverages = SubscriptionOfferStore.all()
            .map(offer => offer.getCoverage())
            .filter((coverage, index, arr) => arr.indexOf(coverage) === index) // ensure unique coverages are returned
            .sort((coverage1, coverage2) => coverage1.id - coverage2.id);
        const cardEntries = [];

        // TODO @cleanup so looks like there should be an option in the panel to be per offer or grouped per coverage
        if (iFrameMerchantService.isMerchant(TNR_ALIAS)) {
            for (const offer of SubscriptionOfferStore.all()) {
                cardEntries.push({
                    coverage: offer.getCoverage(),
                    offers: [offer]
                });
            }
        } else {
            for (const coverage of subscriptionCoverages) {
                cardEntries.push({
                    coverage,
                    offers: [coverage.getAvailableOffers()]
                });
            }
        }

        if (!this.analyticsDispatched) {
            dispatchAnalyticsEvent(AnalyticsEventType.SHOW_SUBSCRIPTION_COVERAGE_CARDS, {
                coverageCards: cardEntries.map(({coverage, offers}) => ({
                    coverageId: coverage.id,
                    offerIds: offers.map(offer => offer.id),
                }))
            });
            this.analyticsDispatched = true;
        }

        const CoverageCardClass = iFrameMerchantService.isMerchant(TNR_ALIAS) ?
            TNRSubscriptionCoverageCard : SubscriptionCoverageCard;

        // TODO @cleanup just add the class to the card?
        let subscriptionCoverageCards = cardEntries.map(({offers, coverage}, index) =>
            <div className={styleSheet.coverageCardContainer}>
                <CoverageCardClass
                    offer={offers[0]}
                    coverage={coverage}
                    bestValue={index === SubscriptionOfferStore.all().length - 1}
                    onSubscriptionOfferSelected={(offer) => this.handleSubscribeClick(offer)}
                />
            </div>);

        if (!subscriptionCoverageCards.length) {
            return [];
        }

        let coveragesRows = [];
        if (subscriptionCoverageCards.length === 4) {
            coveragesRows.push(...[
                <div className={styleSheet.coveragesRow}>{subscriptionCoverageCards.splice(0, 2)}</div>,
                <div className={styleSheet.coveragesRow}>{subscriptionCoverageCards.splice(0, 2)}</div>
            ]);
        } else {
            coveragesRows = [<div className={styleSheet.coveragesRow}>{subscriptionCoverageCards}</div>]
        }

        const addListExtraPadding = subscriptionCoverageCards.length === 4 || subscriptionCoverageCards.length < 3;
        return <div className={styleSheet.coveragesList + (addListExtraPadding ? styleSheet.extraPadding : "")}>
            {coveragesRows}
        </div>
    }

    getSubscriptionText() {
        const {styleSheet} = this;
        const panelText = iFrameState.iframeParams.text; // TODO @mustfix shit is also here
        const {subscribeImageLogo} = iFrameState.iframeParams;
        const currentSubscription = iFrameUserDataService.getActiveSubscription();
        const {subscriptionPlanState} = Controller.subscriptionPlan;

        if (subscriptionPlanState === SUBSCRIBE_PLAN_STATE.editSubscription) {
            return <div className={styleSheet.message + styleSheet.panelTextStyle}>
                {Messages.currentSubscriptionPlan(currentSubscription.coverage.name, currentSubscription.billingPlan.getCycleDuration(true))}
            </div>
        }

        return <div className={styleSheet.subscriptionText}>
            {subscribeImageLogo && <PanelImage src={subscribeImageLogo} className={styleSheet.subscribeImageLogo}/>}
            {panelText && <PanelText className={styleSheet.panelTextStyle}>{panelText}</PanelText>}
        </div>;
    }

    render() {
        const {styleSheet} = this;
        const merchant = iFrameMerchantService.getMerchant();
        const {footerText} = iFrameState;

        return [
            this.getSubscriptionText(),
            this.getAvailableSubscriptionCoveragesListElement(),
            SubscriptionFlowHandler.hasDiscountCodeSupport() ?
                <DiscountCodeInput className={styleSheet.discountCodeInput}
                                   initialValue={this.isDiscountApplied() ? Controller.subscriptionPlan.discountCode : ""}
                                   discountApplied={this.isDiscountApplied()}
                                   onCodeInput={code => this.checkDiscountCode(code)}
                />
                : null,
            iFrameMerchantService.isMerchant(SOUTHERN_THING_ALIAS)
            && Controller.subscriptionPlan.subscriptionPlanState === SUBSCRIBE_PLAN_STATE.subscribe &&
                <CheckboxInput ref="shareNameCheckbox" testId={"shareNameCheckbox"}
                                 className={styleSheet.shareNameCheckbox}
                                 initialValue={Controller.subscriptionPlan.shareName}
                                 onChange={enabled => this.handleShareNameCheckboxToggle(enabled)}
                                 label={Messages.subscribeShareUserName(merchant.getName())}
                />,
            <div className={styleSheet.panelFooterSeparatorStyle}/>,
            footerText && <PanelText className={styleSheet.panelFooterTextStyle}>{footerText}</PanelText>,
            Controller.subscriptionPlan.onSelectCancel === NOOP_FUNCTION ? null :
            <div onClick={() => Controller.subscriptionPlan.onSelectCancel()} className={styleSheet.backButton}>
                {Messages.back}
            </div>,
            Controller.subscriptionPlan.subscriptionPlanState === SUBSCRIBE_PLAN_STATE.subscribe
                && !iFrameMerchantService.isMerchant(TNR_ALIAS) ? <PoweredByBlink/> : null,
        ]
    }

    isDiscountApplied() {
        return !!Controller.subscriptionPlan.discountCode;
    }

    async checkDiscountCode(code) {
        Controller.subscriptionPlan.update({discountCode: code});
        await Controller.subscriptionPlan.fetchPrices(Controller.address?.selectedAddress);
        this.redraw();
    }

    async handleSubscribeClick(offer) {
        Controller.subscriptionPlan.update({selectedOffer: offer});
        dispatchAnalyticsEvent(AnalyticsEventType.SUBSCRIPTION_SELECTED, {offerId: offer.id});

        await iFrameAuthService.promptAuthenticationIfNeeded();

        if (Controller.subscriptionPlan.subscriptionPlanState === SUBSCRIBE_PLAN_STATE.editSubscription) {
            const instantPayInfo = Controller.subscriptionPlan.getDiscountInfoOnInstantChangePolicy(offer);

            Controller.subscriptionPlan.update({
                applyPolicy: instantPayInfo?.coverageChangeType == CoverageChangeType.UPGRADE ?
                    SubscriptionApplyPolicy.INSTANTLY : SubscriptionApplyPolicy.NEXT_BILLING_CYCLE
            });
        }
        Controller.subscriptionPlan.onSelectSuccess();
    }

    handleShareNameCheckboxToggle(enabled) {
        Controller.subscriptionPlan.update({shareName: enabled});
        this.redraw();
    }

    async onMount() {
        super.onMount();

        this.attachUpdateListener(iFrameState, () => this.redraw());
        this.attachChangeListener(UserAddressStore, () => this.redraw());

        // TODO: This should just disappear. But at this point I am afraid to remove it right now (I am 2 hours behind a release rn).
        this.attachListener(Controller.subscriptionPlan, () => this.redraw());
    }
}
